import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import PropTypes from "prop-types";
import bcrypt from "bcryptjs";
import moment from "moment";

import Alert from "@mui/material/Alert";
import Autocomplete from "@mui/material/Autocomplete";
import Button from "@mui/material/Button";
import Card from "@mui/material/Card";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import Grid from "@mui/material/Grid";
import IconButton from "@mui/material/IconButton";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TablePagination from "@mui/material/TablePagination";
import TableRow from "@mui/material/TableRow";
import TextField from "@mui/material/TextField";
import RefreshIcon from "@mui/icons-material/Refresh";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import TableChartIcon from "@mui/icons-material/TableChart";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";
import { getFinancialReport, manageFinancialAdsMap, manageFinancialAdjust, setAdmin } from "features/slices";
import { canSeeFinancialReport } from "utils/access";
import { NotificationManager } from "react-notifications";

const BUSINESS_OFFSET_MINUTES = -240;
const DEFAULT_END_DATE = moment().utcOffset(BUSINESS_OFFSET_MINUTES).format("YYYY-MM-DD");
const DEFAULT_START_DATE = moment()
  .utcOffset(BUSINESS_OFFSET_MINUTES)
  .startOf("month")
  .format("YYYY-MM-DD");
const FALLBACK_REPORT_TABS = [
  { key: "all", name: "Tất cả" },
  { key: "zonemarts", name: "zonemarts.com" },
  { key: "kingdomshirt", name: "kingdomshirt.com" },
  { key: "homestorie", name: "homestorie.com" },
];

const EMPTY_REPORT = {
  rows: [],
  reportTabs: FALLBACK_REPORT_TABS,
  totals: {},
  sources: {},
  report: { adsAccounts: [], accountColumns: [], expectedCostRate: 8, costRateEditable: true, refundEditable: true },
};

const C = {
  ink: "#0f172a",
  mute: "#64748b",
  line: "#e8edf3",
  wash: "#f7f9fc",
  paper: "#ffffff",
  profit: "#fff1f2",
  profitText: "#be123c",
  ads: "#fffbeb",
  adsText: "#b45309",
  rev: "#ecfdf5",
  revText: "#047857",
  teal: "#0f766e",
  tealSoft: "#ccfbf1",
};

const controlSx = {
  minWidth: 150,
  "& .MuiOutlinedInput-root": {
    minHeight: 38,
    borderRadius: "10px",
    backgroundColor: "#fff",
    fontSize: "0.8rem",
  },
};

const cardSx = {
  border: `1px solid ${C.line}`,
  boxShadow: "0 8px 24px rgba(15, 23, 42, 0.04)",
  borderRadius: "16px",
  overflow: "hidden",
  bgcolor: C.paper,
};

const SHEET_COLORS = {
  accountHeader: C.wash,
  metricHeader: C.wash,
  profit: C.profit,
  ads: C.ads,
  expectedCost: C.paper,
  revenue: C.rev,
  plain: C.paper,
  orderText: C.ink,
  extraHeader: C.wash,
};

const ACCOUNT_METRICS = [
  { key: "netRevenue", label: "REV", background: C.rev, valueColor: C.revText },
  { key: "basecost", label: "Cost" },
  { key: "adsCost", label: "Ads", background: C.ads, valueColor: C.adsText },
  { key: "refundAmount", label: "RF" },
  { key: "profit", label: "Total", background: C.profit, valueColor: C.profitText },
  { key: "orders", label: "Đơn", type: "number" },
  { key: "roas", label: "ROAS", type: "percent" },
];

function headSx(extra = {}) {
  return {
    bgcolor: C.wash,
    color: C.mute,
    fontSize: "0.68rem",
    fontWeight: 700,
    letterSpacing: "0.02em",
    whiteSpace: "nowrap",
    py: 1,
    px: 1.25,
    borderBottom: `1px solid ${C.line}`,
    borderRight: "none",
    ...extra,
  };
}

function cellSx(bg = C.paper, extra = {}) {
  return {
    fontVariantNumeric: "tabular-nums",
    whiteSpace: "nowrap",
    bgcolor: bg,
    color: C.ink,
    fontSize: "0.78rem",
    fontWeight: 600,
    py: 0.85,
    px: 1.25,
    borderBottom: `1px solid ${C.line}`,
    borderRight: "none",
    ...extra,
  };
}

function createVersion(user) {
  return new Promise((resolve, reject) => {
    bcrypt.genSalt(10, (saltError, salt) => {
      if (saltError) {
        reject(saltError);
        return;
      }
      bcrypt.hash(`${user}đố tuấn anh phá được`, salt, (hashError, hash) => {
        if (hashError) reject(hashError);
        else resolve(hash);
      });
    });
  });
}

function formatCurrency(value) {
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(value) || 0);
}

function formatNumber(value) {
  return new Intl.NumberFormat("en-US").format(Number(value) || 0);
}

function formatPercent(value) {
  if (value == null || !Number.isFinite(Number(value))) return "—";
  return `${Number(value).toFixed(2)}%`;
}

function formatValue(value, type) {
  if (type === "number") return formatNumber(value);
  if (type === "percent") return formatPercent(value);
  return formatCurrency(value);
}

function accountOnRow(row, column) {
  const accounts = row.accounts || [];
  return (
    accounts.find((item) => item.key === column.key && item.gid === column.gid) ||
    accounts.find((item) => item.gid && item.gid === column.gid) || {
      netRevenue: 0,
      basecost: 0,
      adsCost: 0,
      refundAmount: 0,
      profit: 0,
      orders: 0,
      roas: null,
    }
  );
}

function ReportValue({ value, type, valueColor }) {
  return (
    <span style={{ color: valueColor || C.ink, fontVariantNumeric: "tabular-nums" }}>
      {formatValue(value, type)}
    </span>
  );
}

ReportValue.defaultProps = {
  type: "currency",
  valueColor: C.ink,
};

ReportValue.propTypes = {
  value: PropTypes.number,
  type: PropTypes.oneOf(["currency", "number", "percent"]),
  valueColor: PropTypes.string,
};

function RefundInput({ value, disabled, onSave }) {
  const [draft, setDraft] = useState(value == null ? "" : String(value));
  useEffect(() => {
    setDraft(value == null ? "" : String(value));
  }, [value]);

  const commit = () => {
    const next = Number(String(draft).replace(/[$,\s]/g, ""));
    if (!Number.isFinite(next) || next < 0) {
      setDraft(value == null ? "" : String(value));
      return;
    }
    if (Number(next) === Number(value || 0)) return;
    onSave(next);
  };

  if (disabled) return <ReportValue value={value} />;

  return (
    <input
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === "Enter") event.currentTarget.blur();
      }}
      style={{
        width: 80,
        border: `1px solid ${C.teal}`,
        borderRadius: 8,
        padding: "4px 6px",
        textAlign: "right",
        fontSize: "0.78rem",
        fontWeight: 600,
        color: C.ink,
        background: C.tealSoft,
        fontVariantNumeric: "tabular-nums",
      }}
    />
  );
}

RefundInput.propTypes = {
  value: PropTypes.number,
  disabled: PropTypes.bool,
  onSave: PropTypes.func.isRequired,
};

function SummaryMetric({ label, value, type, background, valueColor }) {
  return (
    <MDBox
      sx={{
        px: 2,
        py: 1.4,
        bgcolor: C.paper,
        borderRight: `1px solid ${C.line}`,
        borderTop: `3px solid ${background ? valueColor || C.teal : "transparent"}`,
      }}
    >
      <MDTypography
        variant="caption"
        sx={{ color: C.mute, fontWeight: 600, fontSize: "0.68rem" }}
      >
        {label}
      </MDTypography>
      <MDTypography
        variant="h6"
        sx={{
          color: valueColor || C.ink,
          fontWeight: 700,
          mt: 0.4,
          fontSize: "1.05rem",
          letterSpacing: "-0.02em",
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {formatValue(value, type)}
      </MDTypography>
    </MDBox>
  );
}

SummaryMetric.defaultProps = {
  type: "currency",
  background: "",
  valueColor: C.ink,
};

SummaryMetric.propTypes = {
  label: PropTypes.string.isRequired,
  value: PropTypes.number.isRequired,
  type: PropTypes.oneOf(["currency", "number", "percent"]),
  background: PropTypes.string,
  valueColor: PropTypes.string,
};

function accountHasData(rows, column) {
  return (rows || []).some((row) => {
    const values = accountOnRow(row, column);
    return (
      Number(values.orders) > 0 ||
      Number(values.adsCost) > 0 ||
      Number(values.netRevenue) > 0 ||
      Number(values.basecost) > 0
    );
  });
}

function SheetFinancialReport() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const user = useSelector((state) => state.user.user);
  const admin = useSelector((state) => state.user.admin === true);
  const permission = useSelector((state) => state.user.permission || []);
  const canAccess = canSeeFinancialReport(admin, permission, user);

  const [startDate, setStartDate] = useState(DEFAULT_START_DATE);
  const [endDate, setEndDate] = useState(DEFAULT_END_DATE);
  const [reportTab, setReportTab] = useState("Tất cả");
  const [costRateInput, setCostRateInput] = useState("8");
  const [adjustSaving, setAdjustSaving] = useState(false);
  const [report, setReport] = useState(EMPTY_REPORT);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [adsMap, setAdsMap] = useState({ mappings: [], sheetAccounts: [], adsTabs: [], gids: [] });
  const [adsStoreInput, setAdsStoreInput] = useState("");
  const [adsGidInput, setAdsGidInput] = useState("");
  const [adsMapLoading, setAdsMapLoading] = useState(false);
  const [adsMapSaving, setAdsMapSaving] = useState(false);
  const [openAdsMap, setOpenAdsMap] = useState(false);
  const [tableView, setTableView] = useState("store");
  const initialLoadStarted = useRef(false);

  const loadAdsMap = useCallback(
    async (requestedTab = reportTab) => {
      if (!canAccess || !user || !requestedTab || requestedTab === "Tất cả") return;
      setAdsMapLoading(true);
      try {
        const version = await createVersion(user);
        const response = await dispatch(
          manageFinancialAdsMap({
            user,
            version,
            action: "list",
            reportTab: requestedTab,
          })
        ).unwrap();

        if (response?.status === 401 || response?.status === 403) {
          NotificationManager.error(response.message, "Không có quyền", 2500);
          if (admin) dispatch(setAdmin(false));
          if (!canSeeFinancialReport(false, permission, user)) {
            navigate("/dashboard", { replace: true });
          }
          return;
        }
        if (!response || response.status !== 0) {
          throw new Error(response?.message || "Không thể tải mapping Ads");
        }
        setAdsMap(response);
      } catch (requestError) {
        NotificationManager.error(
          requestError?.message || "Không thể tải mapping Ads",
          "Ads mapping",
          2500
        );
      } finally {
        setAdsMapLoading(false);
      }
    },
    [canAccess, dispatch, navigate, permission, reportTab, user]
  );

  const loadReport = useCallback(
    async (forceRefresh = false, requestedTab = reportTab) => {
      if (!canAccess || !user) return;
      setLoading(true);
      setError("");
      try {
        const version = await createVersion(user);
        const response = await dispatch(
          getFinancialReport({
            user,
            version,
            startDate,
            endDate,
            reportTab: requestedTab,
            forceAdsRefresh: forceRefresh,
            forceSheetRefresh: forceRefresh,
            forceStoreRefresh: forceRefresh,
          })
        ).unwrap();

        if (response?.status === 401 || response?.status === 403) {
          NotificationManager.error(response.message, "Không có quyền", 2500);
          if (admin) dispatch(setAdmin(false));
          if (!canSeeFinancialReport(false, permission, user)) {
            navigate("/dashboard", { replace: true });
          }
          return;
        }
        if (!response || response.status !== 0) {
          throw new Error(response?.message || "Không thể tải báo cáo");
        }
        setReport(response);
        setReportTab(response.reportTab || requestedTab);
        setCostRateInput(
          response.report?.expectedCostRate == null
            ? "8"
            : String(response.report.expectedCostRate)
        );
        setPage(0);
        loadAdsMap(response.reportTab || requestedTab);
      } catch (requestError) {
        const message =
          requestError?.message || "Không thể kết nối tới financial report API";
        setError(message);
        NotificationManager.error(message, "Financial report", 2500);
      } finally {
        setLoading(false);
      }
    },
    [admin, canAccess, dispatch, endDate, loadAdsMap, navigate, permission, reportTab, startDate, user]
  );

  useEffect(() => {
    if (!canAccess) {
      navigate("/dashboard", { replace: true });
      return;
    }
    if (!initialLoadStarted.current) {
      initialLoadStarted.current = true;
      loadReport(false);
    }
  }, [canAccess, loadReport, navigate]);

  const handleAddAdsMap = async () => {
    const adsStore = String(adsStoreInput || "").trim();
    const gid = String(adsGidInput || "").trim();
    if (!adsStore || !gid || adsMapSaving) return;
    setAdsMapSaving(true);
    try {
      const version = await createVersion(user);
      const response = await dispatch(
        manageFinancialAdsMap({
          user,
          version,
          action: "create",
          reportTab,
          adsStore,
          gid,
        })
      ).unwrap();
      if (!response || response.status !== 0) {
        throw new Error(response?.message || "Không thể thêm Ads");
      }
      NotificationManager.success(`Đã gắn ${adsStore} với ${gid}`, "Ads mapping", 2000);
      setAdsStoreInput("");
      setAdsGidInput("");
      await loadAdsMap(reportTab);
      await loadReport(true, reportTab);
    } catch (requestError) {
      NotificationManager.error(
        requestError?.message || "Không thể thêm Ads",
        "Ads mapping",
        2500
      );
    } finally {
      setAdsMapSaving(false);
    }
  };

  const handleDeleteAdsMap = async (mapping) => {
    if (!mapping?.id || adsMapSaving) return;
    setAdsMapSaving(true);
    try {
      const version = await createVersion(user);
      const response = await dispatch(
        manageFinancialAdsMap({
          user,
          version,
          action: "delete",
          reportTab,
          mappingId: mapping.id,
        })
      ).unwrap();
      if (!response || response.status !== 0) {
        throw new Error(response?.message || "Không thể xóa mapping Ads");
      }
      NotificationManager.success(`Đã gỡ ${mapping.ads_store}`, "Ads mapping", 2000);
      await loadAdsMap(reportTab);
      await loadReport(true, reportTab);
    } catch (requestError) {
      NotificationManager.error(
        requestError?.message || "Không thể xóa mapping Ads",
        "Ads mapping",
        2500
      );
    } finally {
      setAdsMapSaving(false);
    }
  };

  const handleSaveCostRate = async () => {
    if (adjustSaving) return;
    if (String(costRateInput) === String(report.report?.expectedCostRate ?? "")) return;
    setAdjustSaving(true);
    try {
      const version = await createVersion(user);
      const response = await dispatch(
        manageFinancialAdjust({
          user,
          version,
          action: "rate",
          reportTab,
          rate: costRateInput,
        })
      ).unwrap();
      if (!response || response.status !== 0) {
        throw new Error(response?.message || "Không thể lưu hệ số CP");
      }
      setCostRateInput(String(response.expectedCostRate));
      NotificationManager.success(`CP nhân ${response.expectedCostRate}%`, "CP dự kiến", 2000);
      await loadReport(false, reportTab);
    } catch (requestError) {
      NotificationManager.error(
        requestError?.message || "Không thể lưu hệ số CP",
        "CP dự kiến",
        2500
      );
    } finally {
      setAdjustSaving(false);
    }
  };

  const handleSaveRefund = async (date, amount) => {
    if (!report.report?.refundEditable || adjustSaving) return;
    setAdjustSaving(true);
    try {
      const version = await createVersion(user);
      const response = await dispatch(
        manageFinancialAdjust({
          user,
          version,
          action: "refund",
          reportTab,
          date,
          refundAmount: amount,
        })
      ).unwrap();
      if (!response || response.status !== 0) {
        throw new Error(response?.message || "Không thể lưu RF");
      }
      NotificationManager.success(`RF ${date}: ${formatCurrency(amount)}`, "Refund", 1800);
      await loadReport(false, reportTab);
    } catch (requestError) {
      NotificationManager.error(requestError?.message || "Không thể lưu RF", "Refund", 2500);
    } finally {
      setAdjustSaving(false);
    }
  };

  const visibleRows = useMemo(
    () =>
      (report.rows || []).slice(
        page * rowsPerPage,
        page * rowsPerPage + rowsPerPage
      ),
    [page, report.rows, rowsPerPage]
  );
  const accountColumns = useMemo(
    () =>
      (report.report?.accountColumns || []).filter((column) =>
        accountHasData(report.rows, column)
      ),
    [report.report?.accountColumns, report.rows]
  );
  const mappedAdsKeys = new Set(
    (adsMap.mappings || []).map((item) => String(item.ads_store || "").toLowerCase())
  );
  const adsTabOptions = (adsMap.adsTabs || []).filter(
    (tab) => !mappedAdsKeys.has(String(tab || "").toLowerCase())
  );

  if (!canAccess) return null;

  const totals = report.totals || {};
  const reportTabs = (report.reportTabs || FALLBACK_REPORT_TABS).map((tab) =>
    typeof tab === "string" ? { key: tab, name: tab } : tab
  );
  const adsSource = report.sources?.ads || {};
  const invalidRange = !startDate || !endDate || startDate > endDate;

  const metricRows = [
    { label: "Lợi nhuận", key: "profit", background: C.profit, valueColor: C.profitText },
    { label: "Tổng Ads", key: "adsCost", background: C.ads, valueColor: C.adsText },
    { label: "CP dự kiến", key: "expectedCost" },
    { label: "REV", key: "netRevenue", background: C.rev, valueColor: C.revText },
    { label: "Basecost", key: "basecost" },
    { label: "RF", key: "refundAmount" },
    { label: "Số đơn", key: "orders", type: "number" },
  ];

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        <Card sx={{ ...cardSx, mb: 2 }}>
          <MDBox
            sx={{
              px: 2.5,
              py: 1.75,
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 1.5,
            }}
          >
            <MDBox>
              <MDTypography
                variant="caption"
                sx={{ color: C.mute, fontWeight: 700, letterSpacing: "0.08em" }}
              >
                FINANCIAL REPORT
              </MDTypography>
              <MDBox display="flex" alignItems="center" gap={1} mt={0.4} flexWrap="wrap">
                <TableChartIcon sx={{ fontSize: 20, color: C.teal }} />
                <MDTypography variant="h6" sx={{ color: C.ink, fontWeight: 700, letterSpacing: "-0.02em" }}>
                  {reportTab}
                </MDTypography>
                <MDBox
                  display="flex"
                  alignItems="center"
                  gap={0.75}
                  sx={{
                    ml: 1,
                    px: 1,
                    py: 0.4,
                    bgcolor: C.tealSoft,
                    borderRadius: "999px",
                  }}
                >
                  <MDTypography variant="caption" sx={{ color: C.teal, fontWeight: 700 }}>
                    CP nhân
                  </MDTypography>
                  <input
                    value={costRateInput}
                    onChange={(event) => setCostRateInput(event.target.value)}
                    onBlur={handleSaveCostRate}
                    onKeyDown={(event) => {
                      if (event.key === "Enter") event.currentTarget.blur();
                    }}
                    disabled={adjustSaving}
                    style={{
                      width: 56,
                      border: `1px solid ${C.teal}`,
                      borderRadius: 8,
                      padding: "4px 6px",
                      textAlign: "right",
                      fontSize: "0.8rem",
                      fontWeight: 700,
                      color: C.ink,
                      background: "#fff",
                    }}
                  />
                  <MDTypography variant="caption" sx={{ color: C.teal, fontWeight: 800 }}>
                    % · mọi store
                  </MDTypography>
                </MDBox>
              </MDBox>
            </MDBox>
            <Chip
              icon={<AccessTimeIcon sx={{ fontSize: "15px !important" }} />}
              label="UTC−4"
              size="small"
              sx={{
                bgcolor: C.tealSoft,
                color: C.teal,
                fontWeight: 700,
                height: 28,
                borderRadius: "999px",
              }}
            />
          </MDBox>

          <MDBox sx={{ px: 2, pb: 1.5 }}>
            <Tabs
              value={reportTab}
              onChange={(event, nextTab) => {
                setReportTab(nextTab);
                loadReport(false, nextTab);
              }}
              variant="scrollable"
              scrollButtons="auto"
              aria-label="Domain financial reports"
              sx={{
                minHeight: 40,
                "& .MuiTab-root": {
                  minHeight: 40,
                  minWidth: "auto",
                  px: 1.5,
                  mr: 0.5,
                  textTransform: "none",
                  fontSize: "0.84rem",
                  fontWeight: 600,
                  color: C.mute,
                  borderRadius: "999px",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: C.teal,
                  bgcolor: C.tealSoft,
                },
                "& .MuiTabs-indicator": { display: "none" },
              }}
            >
              {reportTabs.map((tab) => (
                <Tab
                  key={tab.key}
                  value={tab.name}
                  label={tab.name}
                  disabled={loading && tab.name !== reportTab}
                />
              ))}
            </Tabs>
          </MDBox>

          <MDBox
            sx={{
              mx: 2,
              mb: 2,
              p: 0.4,
              display: "inline-flex",
              bgcolor: C.wash,
              borderRadius: "12px",
              border: `1px solid ${C.line}`,
            }}
          >
            {[
              { value: "store", label: "Tài chính" },
              { value: "ads", label: "Thống kê Ads" },
            ].map((item) => (
              <Button
                key={item.value}
                onClick={() => {
                  setTableView(item.value);
                  setPage(0);
                }}
                sx={{
                  minHeight: 34,
                  px: 2,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  fontSize: "0.8rem",
                  boxShadow: "none",
                  color: tableView === item.value ? "#fff" : C.mute,
                  bgcolor: tableView === item.value
                    ? item.value === "ads"
                      ? C.adsText
                      : C.teal
                    : "transparent",
                  "&:hover": {
                    bgcolor: tableView === item.value
                      ? item.value === "ads"
                        ? "#92400e"
                        : "#0d5c56"
                      : "#eef2f7",
                  },
                }}
              >
                {item.label}
              </Button>
            ))}
          </MDBox>

          <MDBox
            sx={{
              px: 2,
              pb: 2,
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1,
            }}
          >
            <TextField
              type="date"
              label="Từ ngày"
              value={startDate}
              onChange={(event) => setStartDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={controlSx}
            />
            <TextField
              type="date"
              label="Đến ngày"
              value={endDate}
              onChange={(event) => setEndDate(event.target.value)}
              InputLabelProps={{ shrink: true }}
              size="small"
              sx={controlSx}
            />
            <Button
              variant="contained"
              onClick={() => loadReport(true)}
              disabled={loading || invalidRange}
              startIcon={
                loading ? <CircularProgress size={15} color="inherit" /> : <RefreshIcon />
              }
              sx={{
                minHeight: 38,
                px: 2,
                borderRadius: "10px",
                textTransform: "none",
                boxShadow: "none",
                fontWeight: 700,
                bgcolor: C.teal,
                color: "#fff",
                "&:hover": { bgcolor: "#0d5c56" },
              }}
            >
              Làm mới
            </Button>
            {reportTab !== "Tất cả" && report.reportStoreKey !== "all" ? (
              <Button
                variant="outlined"
                onClick={() => setOpenAdsMap(true)}
                sx={{
                  minHeight: 38,
                  px: 2,
                  borderRadius: "10px",
                  textTransform: "none",
                  fontWeight: 700,
                  color: C.adsText,
                  borderColor: "#f6d48a",
                  bgcolor: C.ads,
                  "&:hover": { bgcolor: "#fde68a", borderColor: C.adsText },
                }}
              >
                Ads accounts
                {(adsMap.mappings || []).length + (adsMap.sheetAccounts || []).length
                  ? ` (${(adsMap.mappings || []).length + (adsMap.sheetAccounts || []).length})`
                  : ""}
              </Button>
            ) : null}
            <MDBox sx={{ ml: { md: "auto" }, textAlign: { xs: "left", md: "right" } }}>
              <MDTypography variant="caption" display="block" sx={{ color: C.mute, fontWeight: 600 }}>
                Ads mới nhất: {adsSource.latestDate || "—"} · {adsSource.sheetCount || 0} tabs
              </MDTypography>
              <MDTypography variant="caption" display="block" sx={{ color: "#94a3b8" }}>
                Cập nhật:{" "}
                {adsSource.fetchedAt
                  ? moment(adsSource.fetchedAt).format("DD/MM HH:mm")
                  : "—"}
              </MDTypography>
            </MDBox>
          </MDBox>
        </Card>

        {invalidRange ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            Khoảng ngày không hợp lệ.
          </Alert>
        ) : null}
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}

        <Card sx={{ ...cardSx, mb: 2 }}>
          <Grid container>
            {metricRows.map((metric) => (
              <Grid item xs={6} md={2} key={metric.key}>
                <SummaryMetric
                  label={metric.label}
                  value={Number(totals[metric.key]) || 0}
                  type={metric.type || "currency"}
                  background={metric.background}
                  valueColor={metric.valueColor}
                />
              </Grid>
            ))}
          </Grid>
        </Card>

        {tableView === "store" ? (
        <Card sx={cardSx}>
          <TableContainer sx={{ maxHeight: "72vh" }}>
            <Table
              stickyHeader
              size="small"
              sx={{
                minWidth: 980,
                "& .MuiTableRow-root:hover td": { filter: "brightness(0.985)" },
              }}
            >
              <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0 }}>
                <TableRow>
                  {[
                    { label: "Ngày", align: "left" },
                    { label: "Lợi nhuận" },
                    { label: "Ads" },
                    { label: "CP dự kiến" },
                    { label: "REV" },
                    { label: "Basecost" },
                    {
                      label: report.report?.refundEditable
                        ? "RF"
                        : "RF (sửa ở từng store)",
                    },
                    { label: "Đơn" },
                  ].map((header) => (
                    <TableCell
                      key={header.label}
                      align={header.align || "right"}
                      sx={headSx()}
                    >
                      {header.label}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={`store-${row.date}`} hover>
                    <TableCell sx={cellSx(C.paper, { minWidth: 92 })}>
                      {moment.utc(row.date).format("DD/MM")}
                    </TableCell>
                    <TableCell align="right" sx={cellSx(C.profit)}>
                      <ReportValue value={row.profit} valueColor={C.profitText} />
                    </TableCell>
                    <TableCell align="right" sx={cellSx(C.ads)}>
                      <ReportValue value={row.adsCost} valueColor={C.adsText} />
                    </TableCell>
                    <TableCell align="right" sx={cellSx()}>
                      <ReportValue value={row.expectedCost} />
                    </TableCell>
                    <TableCell align="right" sx={cellSx(C.rev)}>
                      <ReportValue value={row.netRevenue} valueColor={C.revText} />
                    </TableCell>
                    <TableCell align="right" sx={cellSx()}>
                      <ReportValue value={row.basecost} />
                    </TableCell>
                    <TableCell align="right" sx={cellSx()}>
                      <RefundInput
                        value={row.refundAmount}
                        disabled={!report.report?.refundEditable || adjustSaving}
                        onSave={(amount) => handleSaveRefund(row.date, amount)}
                      />
                    </TableCell>
                    <TableCell align="right" sx={cellSx()}>
                      <ReportValue value={row.orders} type="number" />
                    </TableCell>
                  </TableRow>
                ))}
                {!loading && visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center" sx={{ py: 5, color: C.mute }}>
                      Không có dữ liệu trong khoảng ngày này.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
          {loading ? (
            <MDBox
              sx={{
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <CircularProgress size={20} />
              <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                Đang tính dữ liệu DB và tải Ads...
              </MDTypography>
            </MDBox>
          ) : null}
          <TablePagination
            component="div"
            count={(report.rows || []).length}
            page={Math.min(
              page,
              Math.max(0, Math.ceil((report.rows || []).length / rowsPerPage) - 1)
            )}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Dòng mỗi trang"
          />
        </Card>
        ) : (
        <Card sx={cardSx}>
          <TableContainer sx={{ maxHeight: "72vh" }}>
            <Table
              stickyHeader
              size="small"
              sx={{ minWidth: Math.max(720, 160 + accountColumns.length * 7 * 72) }}
            >
              <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0 }}>
                <TableRow>
                  <TableCell
                    sx={headSx({
                      position: "sticky",
                      left: 0,
                      zIndex: 4,
                      minWidth: 72,
                    })}
                  >
                    Ngày
                  </TableCell>
                  <TableCell
                    align="right"
                    sx={headSx({
                      position: "sticky",
                      left: 72,
                      zIndex: 4,
                      bgcolor: C.profit,
                      color: C.ink,
                      minWidth: 88,
                    })}
                  >
                    Tổng
                  </TableCell>
                  {accountColumns.map((account) => (
                    <TableCell
                      key={`${account.key}-${account.gid}`}
                      align="center"
                      colSpan={ACCOUNT_METRICS.length}
                      sx={headSx({
                        color: C.ink,
                        borderRight: `2px solid ${C.line}`,
                      })}
                    >
                      {account.name}
                      {account.gid ? (
                        <MDTypography
                          variant="caption"
                          display="block"
                          sx={{ color: C.mute, fontWeight: 600, fontSize: "0.62rem", lineHeight: 1.2 }}
                        >
                          {account.gid}
                        </MDTypography>
                      ) : null}
                    </TableCell>
                  ))}
                </TableRow>
                <TableRow>
                  <TableCell
                    sx={headSx({ position: "sticky", left: 0, zIndex: 4 })}
                  />
                  <TableCell
                    sx={headSx({
                      position: "sticky",
                      left: 72,
                      zIndex: 4,
                      bgcolor: C.profit,
                    })}
                  />
                  {accountColumns.flatMap((account) =>
                    ACCOUNT_METRICS.map((metric) => (
                      <TableCell
                        key={`${account.key}-${account.gid}-${metric.key}`}
                        align="right"
                        sx={headSx({
                          borderRight:
                            metric.key === "roas" ? `2px solid ${C.line}` : `1px solid ${C.line}`,
                        })}
                      >
                        {metric.label}
                      </TableCell>
                    ))
                  )}
                </TableRow>
              </TableHead>
              <TableBody>
                {visibleRows.map((row) => (
                  <TableRow key={row.date} hover>
                    <TableCell
                      sx={cellSx(C.paper, {
                        position: "sticky",
                        left: 0,
                        zIndex: 2,
                        minWidth: 72,
                      })}
                    >
                      {moment.utc(row.date).format("DD/MM")}
                    </TableCell>
                    <TableCell
                      align="right"
                      sx={cellSx(C.profit, {
                        position: "sticky",
                        left: 72,
                        zIndex: 2,
                      })}
                    >
                      <ReportValue
                        value={row.adsTotals?.profit ?? row.profit}
                        valueColor={C.profitText}
                      />
                    </TableCell>
                    {accountColumns.flatMap((account) => {
                      const values = accountOnRow(row, account);
                      return ACCOUNT_METRICS.map((metric) => (
                        <TableCell
                          key={`${row.date}-${account.key}-${account.gid}-${metric.key}`}
                          align="right"
                          sx={cellSx(metric.background || C.paper, {
                            borderRight:
                              metric.key === "roas" ? `2px solid ${C.line}` : `1px solid ${C.line}`,
                          })}
                        >
                          <ReportValue
                            value={values[metric.key]}
                            type={metric.type || "currency"}
                            valueColor={metric.valueColor}
                          />
                        </TableCell>
                      ));
                    })}
                  </TableRow>
                ))}
                {!loading && accountColumns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={2} align="center" sx={{ py: 5, color: C.mute }}>
                      Không có Ads nào phát sinh dữ liệu trong khoảng ngày này.
                    </TableCell>
                  </TableRow>
                ) : !loading && visibleRows.length === 0 ? (
                  <TableRow>
                    <TableCell
                      colSpan={2 + accountColumns.length * ACCOUNT_METRICS.length}
                      align="center"
                      sx={{ py: 5, color: C.mute }}
                    >
                      Không có dữ liệu trong khoảng ngày này.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>

          {loading ? (
            <MDBox
              sx={{
                py: 4,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.25,
                borderTop: "1px solid #e2e8f0",
              }}
            >
              <CircularProgress size={20} />
              <MDTypography variant="body2" sx={{ color: "#64748b" }}>
                Đang tính dữ liệu DB và tải Ads...
              </MDTypography>
            </MDBox>
          ) : null}

          <TablePagination
            component="div"
            count={(report.rows || []).length}
            page={Math.min(
              page,
              Math.max(0, Math.ceil((report.rows || []).length / rowsPerPage) - 1)
            )}
            onPageChange={(event, nextPage) => setPage(nextPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(event) => {
              setRowsPerPage(Number(event.target.value));
              setPage(0);
            }}
            rowsPerPageOptions={[25, 50, 100]}
            labelRowsPerPage="Dòng mỗi trang"
          />
        </Card>
        )}
      </MDBox>
      <Dialog
        open={openAdsMap}
        onClose={() => setOpenAdsMap(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle sx={{ fontWeight: 800 }}>
          Ads accounts · {reportTab}
        </DialogTitle>
        <DialogContent>
          <MDTypography variant="caption" display="block" sx={{ color: "#64748b", mb: 1.5 }}>
            Gắn tab Ads với GID. GID mới tự vào từ API đơn; chỉ cần thêm khi có tab Ads mới.
          </MDTypography>
          <MDBox
            sx={{
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: 1.25,
              mb: 2,
            }}
          >
            <Autocomplete
              freeSolo
              options={adsTabOptions}
              inputValue={adsStoreInput}
              onInputChange={(event, value) => setAdsStoreInput(value)}
              sx={{ minWidth: 220, flex: "1 1 200px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Tên tab Ads"
                  size="small"
                  placeholder="Ví dụ: Zonemarts7"
                  sx={controlSx}
                />
              )}
            />
            <Autocomplete
              freeSolo
              options={(adsMap.gids || []).map((item) => item.gid)}
              inputValue={adsGidInput}
              onInputChange={(event, value) => setAdsGidInput(value)}
              sx={{ minWidth: 220, flex: "1 1 200px" }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="GID"
                  size="small"
                  placeholder="AW-17808705977"
                  sx={controlSx}
                />
              )}
            />
            <Button
              variant="contained"
              onClick={handleAddAdsMap}
              disabled={
                adsMapSaving ||
                !String(adsStoreInput || "").trim() ||
                !String(adsGidInput || "").trim()
              }
              sx={{
                minHeight: 40,
                px: 2,
                borderRadius: "8px",
                textTransform: "none",
                boxShadow: "none",
                fontWeight: 800,
              }}
            >
              {adsMapSaving ? "Đang lưu..." : "Thêm Ads"}
            </Button>
          </MDBox>
          <TableContainer>
            <Table size="small">
              <TableHead sx={{ display: "table-header-group", p: 0, borderRadius: 0 }}>
                <TableRow>
                  {["Tab Ads", "GID", "Store", "Nguồn", ""].map((header) => (
                    <TableCell
                      key={header || "action"}
                      sx={headSx()}
                    >
                      {header}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {(adsMap.mappings || []).map((mapping) => (
                  <TableRow key={mapping.id}>
                    <TableCell sx={{ fontWeight: 700 }}>{mapping.ads_store}</TableCell>
                    <TableCell sx={{ fontWeight: 700 }}>{mapping.gid || "—"}</TableCell>
                    <TableCell>{mapping.store}</TableCell>
                    <TableCell>
                      <Chip label="Dashboard" size="small" sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => handleDeleteAdsMap(mapping)}
                        disabled={adsMapSaving}
                      >
                        <DeleteOutlineIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
                {(adsMap.sheetAccounts || []).map((account) => (
                  <TableRow key={`sheet-${account}`}>
                    <TableCell sx={{ fontWeight: 700 }}>{account}</TableCell>
                    <TableCell>—</TableCell>
                    <TableCell>{reportTab}</TableCell>
                    <TableCell>
                      <Chip label="Sheet cũ" size="small" variant="outlined" sx={{ fontWeight: 700 }} />
                    </TableCell>
                    <TableCell />
                  </TableRow>
                ))}
                {!adsMapLoading &&
                !(adsMap.mappings || []).length &&
                !(adsMap.sheetAccounts || []).length ? (
                  <TableRow>
                    <TableCell colSpan={5} align="center" sx={{ py: 3, color: "#64748b" }}>
                      Chưa có Ads account cho store này.
                    </TableCell>
                  </TableRow>
                ) : null}
              </TableBody>
            </Table>
          </TableContainer>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenAdsMap(false)} sx={{ textTransform: "none", fontWeight: 800 }}>
            Đóng
          </Button>
        </DialogActions>
      </Dialog>
      <Footer />
    </DashboardLayout>
  );
}

export default SheetFinancialReport;
